import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  List,
  Loading,
  SelectInput,
  TextInput,
  useDataProvider,
  useListContext,
  useNotify,
  useRedirect,
} from "react-admin";
import { JsonDataView } from "../components/JsonDataView";
import { JsonEntryRows, removeEntryAtPath, tryParseJsonString, updateValueAtPath } from "../components/JsonEntryRows";

const RESOURCE = "firebaseNotificationMessage";

const MessageListFilters = [
  <TextInput key="name-filter" label="Notification Name" source="name__icontains" alwaysOn />,
  <TextInput key="language-filter" label="Notification Language" source="language__icontains" />,
  <SelectInput
    key="active-filter"
    source="active"
    label="Active"
    choices={[
      { id: "true", name: "Active" },
      { id: "false", name: "Inactive" },
    ]}
    fullWidth
  />,
];

const encodeGroupParam = (value: string) => encodeURIComponent(value ?? "");
const buildGroupPath = (mode: "show" | "edit", name: string, appId: string) =>
  `/firebase-notifications/${encodeGroupParam(name)}${appId ? `/${encodeGroupParam(appId)}` : ""}/${mode}`;
const buildGroupKey = (name: string, appId: string) => `${name}__${appId}`;

type MessageGroup = {
  name: string;
  appId: string;
  languages: string[];
  active: boolean;
};

// Notifications with the same name+appId are variants of one campaign shown in different languages,
// so the list groups them into a single row instead of one row per language.
const GroupedMessageTable = () => {
  const { data, isLoading } = useListContext();

  const groups = useMemo<MessageGroup[]>(() => {
    const map = new Map<string, MessageGroup>();
    (data || []).forEach((record: any) => {
      const name = record.name ?? "";
      const appId = record.appId ?? "";
      const key = buildGroupKey(name, appId);
      const existing = map.get(key);
      if (existing) {
        existing.languages.push(record.language ?? "");
        existing.active = existing.active || Boolean(record.active);
      } else {
        map.set(key, { name, appId, languages: [record.language ?? ""], active: Boolean(record.active) });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>App ID</TableCell>
            <TableCell>Languages</TableCell>
            <TableCell>Active</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <TableRow key={buildGroupKey(group.name, group.appId)}>
              <TableCell>{group.name}</TableCell>
              <TableCell>{group.appId}</TableCell>
              <TableCell>{group.languages.join(", ")}</TableCell>
              <TableCell>{group.active ? "Active" : "Inactive"}</TableCell>
              <TableCell align="right">
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    component={RouterLink}
                    to={buildGroupPath("show", group.name, group.appId)}
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon fontSize="small" />}
                  >
                    Show
                  </Button>
                  <Button
                    component={RouterLink}
                    to={buildGroupPath("edit", group.name, group.appId)}
                    size="small"
                    variant="contained"
                    startIcon={<EditIcon fontSize="small" />}
                  >
                    Edit
                  </Button>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const MessageList = () => (
  <List
    resource={RESOURCE}
    filters={MessageListFilters}
    pagination={false}
    perPage={1000}
    sort={{ field: "name", order: "ASC" }}
  >
    <GroupedMessageTable />
  </List>
);

// Notification titles/bodies are stored as JSON maps of variant index -> localized text,
// e.g. {"0": "A Dayo manca un amico!", "1": "È ora di spazzolare i denti! 🦷"}.
const NotificationVariantsEditor: React.FC<{
  label: string;
  value: any;
  readOnly?: boolean;
  onChange?: (next: any) => void;
}> = ({ label, value, readOnly = false, onChange }) => {
  const [pendingDelete, setPendingDelete] = useState<{ entryKey: string | number; label: string } | null>(null);
  const isStructuredData = value !== null && typeof value === "object";

  const handlePrimitiveChange = (path: Array<string | number>, nextValue: string) => {
    onChange?.(updateValueAtPath(value, path, nextValue));
  };

  const handleAddVariant = () => {
    const base = value && typeof value === "object" ? value : {};
    const isArrayBase = Array.isArray(base);
    const nextKey = isArrayBase ? String(base.length) : String(Object.keys(base).length);
    const next = isArrayBase ? [...base, ""] : { ...base, [nextKey]: "" };
    onChange?.(next);
  };

  const handleDeleteVariant = (entryKey: string | number) => {
    onChange?.(removeEntryAtPath(value, [], entryKey));
  };

  const requestDeleteVariant = (entryKey: string | number, entryLabel: string) => {
    setPendingDelete({ entryKey, label: entryLabel });
  };

  const confirmDeleteVariant = () => {
    if (!pendingDelete) {
      return;
    }
    handleDeleteVariant(pendingDelete.entryKey);
    setPendingDelete(null);
  };

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      {!readOnly ? (
        isStructuredData ? (
          <>
            <JsonEntryRows
              value={value}
              keyEditable={false}
              formatKeyLabel={(key) => String(Number(key) + 1)}
              onPrimitiveChange={handlePrimitiveChange}
              onDeleteRequest={(_containerPath, entryKey, entryLabel) => requestDeleteVariant(entryKey, entryLabel)}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddVariant}
              sx={{ alignSelf: "flex-start" }}
            >
              Add Variant
            </Button>
          </>
        ) : (
          <JsonDataView data={value} emptyText={`No ${label.toLowerCase()} data`} />
        )
      ) : isStructuredData ? (
        <JsonEntryRows value={value} keyEditable={false} formatKeyLabel={(key) => String(Number(key) + 1)} readOnly />
      ) : (
        <JsonDataView data={value} emptyText={`No ${label.toLowerCase()} data`} />
      )}

      <Dialog open={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Delete variant?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <b>{pendingDelete?.label || "this variant"}</b>? This change will be saved
            when you click Update.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon fontSize="small" />} onClick={() => setPendingDelete(null)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            onClick={confirmDeleteVariant}
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

type LanguageEntry = {
  key: string;
  id?: string;
  language: string;
  active: boolean;
  title: any;
  body: any;
};

const emptyVariants = () => ({ "0": "" });

let languageKeySeq = 0;
const nextLanguageKey = () => `new-${(languageKeySeq += 1)}`;

const toLanguageEntry = (record: any): LanguageEntry => ({
  key: String(record.id),
  id: String(record.id),
  language: record.language ?? "",
  active: Boolean(record.active),
  title: tryParseJsonString(record.title),
  body: tryParseJsonString(record.body),
});

const AddLanguageDialog: React.FC<{
  open: boolean;
  existingLanguages: string[];
  onClose: () => void;
  onAdd: (language: string) => void;
}> = ({ open, existingLanguages, onClose, onAdd }) => {
  const [language, setLanguage] = useState("");
  const trimmed = language.trim();
  const isDuplicate = existingLanguages.some((existing) => existing.toLowerCase() === trimmed.toLowerCase());

  useEffect(() => {
    if (open) {
      setLanguage("");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add language</DialogTitle>
      <DialogContent>
        <MuiTextField
          autoFocus
          fullWidth
          size="small"
          label="Language code"
          placeholder="e.g. en, de, fr"
          value={language}
          error={isDuplicate}
          helperText={isDuplicate ? "This language already exists" : " "}
          onChange={(event) => setLanguage(event.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button startIcon={<CloseIcon fontSize="small" />} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon fontSize="small" />}
          disabled={!trimmed || isDuplicate}
          onClick={() => onAdd(trimmed)}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LanguageSections: React.FC<{
  entries: LanguageEntry[];
  readOnly?: boolean;
  onEntryChange?: (key: string, patch: Partial<LanguageEntry>) => void;
  onEntryDelete?: (key: string) => void;
  onAddLanguage?: (language: string) => void;
}> = ({ entries, readOnly = false, onEntryChange, onEntryDelete, onAddLanguage }) => {
  const [addOpen, setAddOpen] = useState(false);
  const [pendingLanguageDelete, setPendingLanguageDelete] = useState<LanguageEntry | null>(null);

  return (
    <>
      <Stack spacing={2}>
        {entries.map((entry) => (
          <Paper key={entry.key} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ flex: 1 }}>
                {(entry.language || "New language").toUpperCase()}
              </Typography>
              {readOnly ? (
                !entry.active && (
                  <Typography variant="body2" color="text.secondary">
                    Inactive
                  </Typography>
                )
              ) : (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={entry.active}
                        onChange={(event) => onEntryChange?.(entry.key, { active: event.target.checked })}
                      />
                    }
                    label="Active"
                  />
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={`Delete ${entry.language}`}
                    onClick={() => setPendingLanguageDelete(entry)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>
            <NotificationVariantsEditor
              label="Title"
              value={entry.title}
              readOnly={readOnly}
              onChange={(next) => onEntryChange?.(entry.key, { title: next })}
            />
            <NotificationVariantsEditor
              label="Body"
              value={entry.body}
              readOnly={readOnly}
              onChange={(next) => onEntryChange?.(entry.key, { body: next })}
            />
          </Paper>
        ))}
      </Stack>

      {!readOnly && (
        <Button variant="outlined" startIcon={<AddIcon />} sx={{ mt: 2 }} onClick={() => setAddOpen(true)}>
          Add Language
        </Button>
      )}

      <AddLanguageDialog
        open={addOpen}
        existingLanguages={entries.map((entry) => entry.language)}
        onClose={() => setAddOpen(false)}
        onAdd={(language) => {
          onAddLanguage?.(language);
          setAddOpen(false);
        }}
      />

      <Dialog open={Boolean(pendingLanguageDelete)} onClose={() => setPendingLanguageDelete(null)}>
        <DialogTitle>Delete language?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <b>{(pendingLanguageDelete?.language || "this language").toUpperCase()}</b>{" "}
            and all of its variants? This change will be saved when you click Update.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon fontSize="small" />} onClick={() => setPendingLanguageDelete(null)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteOutlineIcon fontSize="small" />}
            onClick={() => {
              if (pendingLanguageDelete) {
                onEntryDelete?.(pendingLanguageDelete.key);
              }
              setPendingLanguageDelete(null);
            }}
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const useLanguageEntries = (initial: LanguageEntry[] | null) => {
  const [entries, setEntries] = useState<LanguageEntry[]>(initial ?? []);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);

  const updateEntry = (key: string, patch: Partial<LanguageEntry>) => {
    setEntries((prev) => prev.map((entry) => (entry.key === key ? { ...entry, ...patch } : entry)));
  };

  const addLanguage = (language: string) => {
    setEntries((prev) => [
      ...prev,
      { key: nextLanguageKey(), language, active: true, title: emptyVariants(), body: emptyVariants() },
    ]);
  };

  const deleteEntry = (key: string) => {
    setEntries((prev) => {
      const target = prev.find((entry) => entry.key === key);
      if (target?.id) {
        setDeletedIds((ids) => [...ids, target.id as string]);
      }
      return prev.filter((entry) => entry.key !== key);
    });
  };

  return { entries, setEntries, deletedIds, setDeletedIds, updateEntry, addLanguage, deleteEntry };
};

export const MessageCreate = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [name, setName] = useState("");
  const [appId, setAppId] = useState("");
  const [saving, setSaving] = useState(false);
  const { entries, updateEntry, addLanguage, deleteEntry } = useLanguageEntries([
    { key: nextLanguageKey(), language: "en", active: true, title: emptyVariants(), body: emptyVariants() },
  ]);

  const canSave = Boolean(name.trim()) && entries.length > 0 && entries.every((entry) => entry.language.trim());

  const handleCreate = async () => {
    setSaving(true);
    try {
      await Promise.all(
        entries.map((entry) =>
          dataProvider.create(RESOURCE, {
            data: {
              name: name.trim(),
              appId: appId.trim() || null,
              language: entry.language.trim(),
              active: entry.active,
              title: JSON.stringify(entry.title),
              body: JSON.stringify(entry.body),
            },
          })
        )
      );
      notify("Notification created", { type: "success" });
      redirect("list", RESOURCE);
    } catch (error) {
      notify("Failed to create notification", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Create Notification
      </Typography>
      <Stack spacing={2} sx={{ mb: 2, maxWidth: 480 }}>
        <MuiTextField
          size="small"
          label="Name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <MuiTextField size="small" label="App ID" value={appId} onChange={(event) => setAppId(event.target.value)} />
      </Stack>

      <LanguageSections
        entries={entries}
        onEntryChange={updateEntry}
        onEntryDelete={deleteEntry}
        onAddLanguage={addLanguage}
      />

      <Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          sx={{ mt: 2 }}
          onClick={handleCreate}
          disabled={saving || !canSave}
        >
          {saving ? "Saving..." : "Create"}
        </Button>
      </Box>
    </Box>
  );
};

type MessageGroupParams = {
  name: string;
  appId?: string;
};

const useMessageGroupRecords = (name: string, appId: string) => {
  const dataProvider = useDataProvider();
  const [records, setRecords] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    dataProvider
      .getList(RESOURCE, {
        pagination: { page: 1, perPage: 200 },
        sort: { field: "language", order: "ASC" },
        filter: appId ? { name, appId } : { name },
      })
      .then((result: any) => {
        if (mounted) {
          setRecords((result.data || []).filter((record: any) => (record.appId ?? "") === appId));
        }
      })
      .catch(() => {
        if (mounted) setRecords([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [dataProvider, name, appId, reloadToken]);

  const reload = () => setReloadToken((token) => token + 1);

  return { records, loading, reload };
};

const GroupHeader: React.FC<{ name: string; appId: string }> = ({ name, appId }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="h5">{name}</Typography>
    {appId && (
      <Typography variant="body2" color="text.secondary">
        App ID: {appId}
      </Typography>
    )}
  </Box>
);

export const MessageShow = () => {
  const { name = "", appId = "" } = useParams<MessageGroupParams>();
  const { records, loading } = useMessageGroupRecords(name, appId);

  if (loading || !records) {
    return <Loading />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <GroupHeader name={name} appId={appId} />
      <LanguageSections entries={records.map(toLanguageEntry)} readOnly />
    </Box>
  );
};

export const MessageEdit = () => {
  const { name = "", appId = "" } = useParams<MessageGroupParams>();
  const { records, loading, reload } = useMessageGroupRecords(name, appId);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { entries, setEntries, deletedIds, setDeletedIds, updateEntry, addLanguage, deleteEntry } =
    useLanguageEntries(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!records) {
      return;
    }
    setEntries(records.map(toLanguageEntry));
    setDeletedIds([]);
  }, [records, setEntries, setDeletedIds]);

  if (loading || !records) {
    return <Loading />;
  }

  const canSave = entries.every((entry) => entry.language.trim());

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = (entry: LanguageEntry) => ({
        language: entry.language.trim(),
        active: entry.active,
        title: JSON.stringify(entry.title),
        body: JSON.stringify(entry.body),
      });

      await Promise.all([
        ...entries.map((entry) =>
          entry.id
            ? dataProvider.update(RESOURCE, {
                id: entry.id,
                data: payload(entry),
                previousData: records.find((record) => String(record.id) === entry.id),
              })
            : dataProvider.create(RESOURCE, {
                data: { name, appId: appId || null, ...payload(entry) },
              })
        ),
        ...deletedIds.map((id) =>
          dataProvider.delete(RESOURCE, {
            id,
            previousData: records.find((record) => String(record.id) === id),
          })
        ),
      ]);
      notify("Notifications updated", { type: "success" });
      reload();
    } catch (error) {
      notify("Failed to update notifications", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <GroupHeader name={name} appId={appId} />
      <LanguageSections
        entries={entries}
        onEntryChange={updateEntry}
        onEntryDelete={deleteEntry}
        onAddLanguage={addLanguage}
      />
      <Box>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          sx={{ mt: 2 }}
          onClick={handleSave}
          disabled={saving || !canSave}
        >
          {saving ? "Saving..." : "Update"}
        </Button>
      </Box>
    </Box>
  );
};
